import React, { useState, useEffect } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import { TESTING_PHASE_BOOKING_COPY } from '../config/sharedTransferRules';

const ExplainerModal = ({
  isOpen,
  onClose,
  onConfirm,
  context = 'join',
  maxAuth = 0,
  minSeats = 2,
  committed = false,
  showDontShowAgain = true,
  localStorageKey = 'explainer_seen_join'
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (!isOpen) setDontShowAgain(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const formattedMaxAuth = maxAuth ? `$${Number(maxAuth).toFixed(2)}` : 'your current price';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fadeIn">
      <div className="absolute inset-0 bg-[#2D1E17]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-slideIn">
        <div className="p-6 border-b border-stone-100 bg-stone-50 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FAF3E0] rounded-xl flex items-center justify-center">
              <ShieldCheck size={20} className="text-[#A44432]" />
            </div>
            <h2 className="text-lg font-serif font-bold text-[#2D1E17]">
              {context === 'join' ? 'Review Pricing & Policy' : 'How pricing and withdrawals work'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-stone-200 rounded-full transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {context === 'join' ? (
            <>
              <PolicyItem title="Price Drop Guarantee" desc={`As more people join, your share drops. You'll never pay more than ${formattedMaxAuth}.`} />
              <PolicyItem title="Payment" desc={`We authorize your card now (up to ${formattedMaxAuth}). We charge 36 hours before departure once the trip is confirmed — or immediately if you join an already-charged trip. The final charge may be lower if more riders join.`} />
              <PolicyItem title="Withdrawal" desc="Withdraw for free anytime before the trip confirms. After confirmation, withdrawals are not allowed." />
              <PolicyItem title="Booking Window" desc={TESTING_PHASE_BOOKING_COPY} />
              <PolicyItem title="Minimum Not Met" desc="If the minimum isn't met 24 hours before departure, the trip is cancelled, you won't be charged, and the authorization is released." />
            </>
          ) : (
            <>
              <PolicyItem title="Group Pricing" desc="Riders' price decreases as more people join." />
              <PolicyItem title="Payment" desc={`Your card is authorized now (up to ${formattedMaxAuth}). We charge 36 hours before departure once the trip confirms; the final amount may be lower if more riders join. You never pay more than the hold.`} />
              <PolicyItem title="Withdrawal" desc="Riders can withdraw for free before confirmation. After confirmation, withdrawals are not allowed." />
              <PolicyItem title="Booking Window" desc={TESTING_PHASE_BOOKING_COPY} />
              {committed ? (
                <PolicyItem title="Guaranteed departure" desc={`This trip confirms now and runs with your group of ${minSeats}. If nobody else joins you pay your party's rate (this hold). If others join you pay the shared rate, never more than ${formattedMaxAuth}.`} />
              ) : (
                <PolicyItem title="Minimum Group" desc={`Trip runs at ${minSeats} riders total (you + others). If the minimum isn't met 24 hours before departure, the trip cancels and holds are released.`} />
              )}
              <PolicyItem title="Confirmation Lock" desc="Once confirmed, the trip is locked and withdrawals aren't possible." />
            </>
          )}
        </div>

        <div className="p-6 border-t border-stone-100">
          {showDontShowAgain && (
            <label className="flex items-center mb-4 text-xs text-stone-500 cursor-pointer">
              <input type="checkbox" className="mr-2 rounded" checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} />
              Don&apos;t show this again
            </label>
          )}
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="btn-ghost">Cancel</button>
            <button
              onClick={() => {
                try {
                  if (showDontShowAgain && dontShowAgain && localStorageKey) {
                    localStorage.setItem(localStorageKey, '1');
                  }
                } catch {}
                onConfirm?.();
              }}
              className="btn-primary"
            >
              Agree and Continue
            </button>
          </div>
          <p className="text-[10px] text-stone-400 mt-3 text-center">
            By continuing, you accept the Pricing &amp; Withdrawal Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

const PolicyItem = ({ title, desc }) => (
  <div className="flex gap-4 p-4 border border-stone-100 rounded-2xl bg-stone-50/50">
    <div className="mt-1"><div className="w-1.5 h-1.5 rounded-full bg-[#A44432]" /></div>
    <div>
      <h4 className="font-bold text-sm text-[#2D1E17]">{title}</h4>
      <p className="text-xs text-stone-500">{desc}</p>
    </div>
  </div>
);

export default ExplainerModal;
